import type { ReactNode } from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';

import type { BoxProps } from '@hivebrite/design-system/Box';
import { Box } from '@hivebrite/design-system/Box';
import { FlagStatus, STATUS } from '@hivebrite/design-system/Flag';
import {
  CATEGORIES,
  FontAwesomeIcon,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { Image } from '@hivebrite/design-system/Image';
import { PillsList } from '@hivebrite/design-system/PillsList';

import { Calendar as CalendarComponent } from 'hb-react/shared/apps/Events/components/Calendar';
import EventPricingTag from 'hb-react/shared/apps/Events/components/EventPricingTag';
import EventDate from 'hb-react/shared/components/EventDate';
import useTimeZoneConfiguration from 'hb-react/shared/hooks/useTimeZoneConfiguration';

import type { Event } from '../types';

export const Spacer = () => <Box flexGrow={1} />;

export const Owner = ({
  event,
}: {
  event: Pick<Event, 'displayEventCreator' | 'parent'>;
}) => {
  if (!event.displayEventCreator || !event.parent) {
    return null;
  }

  return (
    <Box gridColumnGap={2} alignItems="center">
      <AvatarBox $withBorder={!event.parent.avatarUrl}>
        {event.parent.avatarUrl ? (
          <Image
            src={event.parent.avatarUrl}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <FontAwesomeIcon
            iconName="user-group"
            iconType={CATEGORIES.DUOTONE}
            color="primary"
            height="10px"
          />
        )}
      </AvatarBox>

      <Box font="body-semibold-asphalt">{event.parent.name}</Box>
    </Box>
  );
};

export const Cancelled = ({ event }: { event: Event }) => {
  if (!event.cancelled) {
    return null;
  }

  return (
    <Box position="absolute" top="8px" left="8px">
      <FlagStatus status={STATUS.ERROR} text={__('Cancelled')} />
    </Box>
  );
};

export const PricingTag = ({ event }: { event: Event }) => {
  const eventProps = useMemo(
    () => ({
      id: event.id,
      display_price_tag: event.displayPriceTag,
      tickets_due_date: event.ticketsDueDate,
      participation_type: event.participationType,
    }),
    [
      event.id,
      event.displayPriceTag,
      event.ticketsDueDate,
      event.participationType,
    ],
  );

  return (
    <Box position="absolute" top="8px" left="8px">
      <EventPricingTag event={eventProps} />
    </Box>
  );
};

export const Calendar = ({ startDate }: { startDate: string }) => (
  <Box position="absolute" left="8px" bottom="5px">
    <CalendarComponent startDate={startDate} />
  </Box>
);

export const Header = ({ children }: { children: ReactNode }) => (
  <Box overflow="hidden" position="relative" height="168px">
    {children}
  </Box>
);

export const Body = ({ children }: { children: ReactNode }) => (
  <Box
    flexGrow={1}
    pt={2}
    px={3}
    pb={4}
    gridRowGap={2}
    flexDirection="column"
    minHeight="200px"
  >
    {children}
  </Box>
);

export const Title = ({ children }: { children: ReactNode }) => (
  <Box font="h3-semibold">{children}</Box>
);

export const StartEndDates = ({
  event,
}: {
  event: Pick<Event, 'startDate' | 'endDate' | 'timezone'>;
}) => {
  const { currentUserTimezone } = useTimeZoneConfiguration();

  const eventProps = useMemo(
    () => ({
      start_date: event.startDate,
      end_date: event.endDate,
      timezone: event.timezone,
    }),
    [event.startDate, event.endDate, event.timezone],
  );

  return (
    <Box display="block">
      <EventDate
        event={eventProps}
        timezoneConfiguration={{
          currentUserTimezone,
        }}
      />
    </Box>
  );
};

export const Location = ({ event }: { event: Pick<Event, 'location'> }) => {
  const displayLocation = event.location?.location ?? event.location?.address;

  if (!displayLocation) {
    return null;
  }

  return (
    <Box gridColumnGap={1} alignItems="center">
      <FontAwesomeIcon
        height="12px"
        iconName="location-dot"
        iconType={CATEGORIES.SOLID}
      />
      <Box>{displayLocation}</Box>
    </Box>
  );
};

export const OnlineEvent = ({
  event,
}: {
  event: Pick<Event, 'webinarUrl' | 'zoomMeetingId'>;
}) => {
  const isOnline = event.webinarUrl || event.zoomMeetingId;

  if (!isOnline) {
    return null;
  }

  return (
    <Box gridColumnGap={1} alignItems="center">
      <FontAwesomeIcon
        height="12px"
        iconName="video"
        iconType={CATEGORIES.SOLID}
      />
      <Box>{__('Online event')}</Box>
    </Box>
  );
};

export const Categories = ({
  event,
  ...props
}: {
  event: Pick<Event, 'networkCategories'>;
} & BoxProps) => {
  if (!event.networkCategories?.length) {
    return null;
  }

  return (
    <Box justifyContent="flex-end" {...props}>
      <PillsList
        pills={event.networkCategories}
        seeMoreLabel={__('See More')}
        seeLessLabel={s__('Tags|See less')}
      />
    </Box>
  );
};

const AvatarBox = styled(Box).attrs({
  width: '24px',
  height: '24px',
  flexShrink: 0,
  overflow: 'hidden',
  justifyContent: 'center',
  alignItems: 'center',
})<{ $withBorder?: boolean }>`
  border-radius: 5px;
  border: ${({ $withBorder, theme }) =>
    $withBorder ? `0.5px solid ${theme.colors.dove}` : 'none'};
`;
